import React, { useMemo } from 'react';
import _, { keys, noop, reduce } from 'lodash';
import { has, isEmpty } from 'lodash';
import { FieldPill } from '../../field-pill';
import {
  FieldPillGroupStyled,
  FieldPillStyled,
  HiddenFieldsHeader,
} from '../viz-layout-panel.styles';
import { FieldDropdown } from '../../../components/field-dropdown';
import { IAnyAttribute } from '../../../datasets/interfaces/attribute.interface';
import { useVizLayoutPanelContext } from '../viz-layout-panel.context';
import { messages } from '../../../i18n';
import { NoMatches } from '../../../components/ui';
import { VirtualizedFieldList } from './virtualized-field-list';

interface VizLayoutFieldsProps {
  onContextMenuAction: (key: string, field: IAnyAttribute) => void;
  isScrolling: boolean;
  expandTimeFields: string[];
  onExpandToggleHandler: (fieldName: string) => void;
  handleScroll: () => void;
}

export const VizLayoutFields: React.FC<VizLayoutFieldsProps> = ({
  onContextMenuAction,
  isScrolling,
  expandTimeFields,
  onExpandToggleHandler,
  handleScroll = noop,
}) => {
  const {
    viz,
    hideFields,
    visibleFields,
    invisibleFields,
    fieldsInPlay,
    showHiddenFields,
    searchFieldsValue,
    isSearchFieldsValueValid,
    getVirtualizationData,
  } = useVizLayoutPanelContext();

  const virtualizationData = useMemo(
    () => getVirtualizationData(expandTimeFields),
    [getVirtualizationData, expandTimeFields],
  );

  const availableFieldCount = reduce(
    visibleFields,
    (counter, field) => {
      if (_.isEmpty(field.children)) {
        counter++;
      } else {
        counter += field.children.length;
      }
      return counter;
    },
    0,
  );

  const renderChildFields = (field: IAnyAttribute) => {
    if (!isEmpty(field.children)) {
      // get the fields that are in play, we won't want to show those in the list
      const hierarchyFields = field.children;
      return hierarchyFields.map((timeField, idx) => {
        return (
          <FieldPill
            field={timeField}
            key={`${timeField.name}-${idx}`}
            renderFieldDropdown={isDragging => (
              <FieldDropdown
                discoveryId={viz?.id}
                field={timeField}
                doMenuAction={key => onContextMenuAction(key, timeField)}
                isScrolling={isScrolling ?? false}
                isDragging={isDragging}
              />
            )}
          />
        );
      });
    }
  };

  const renderFields = (fieldsToRender = []) => {
    return _(fieldsToRender)
      .filter(f => !has(hideFields, f.name))
      .reduce((acc, f, idx) => {
        if (!isEmpty(f.children)) {
          const childFields = renderChildFields(f);
          // only render the group if there are children in it (example: not all children in play)
          if (!isEmpty(childFields)) {
            const fieldPillRoot = (
              <FieldPill
                field={f}
                iconDataType={'HIERARCHY'}
                key={`${f.name}-${idx}`}
                renderFieldDropdown={isDragging => (
                  <FieldDropdown
                    discoveryId={viz?.id}
                    field={f}
                    doMenuAction={key => onContextMenuAction(key, f)}
                    isScrolling={isScrolling ?? false}
                    isDragging={isDragging}
                  />
                )}
              />
            );
            const forceExpanded = _.includes(expandTimeFields, f.name);
            acc.push(
              <FieldPillGroupStyled
                root={fieldPillRoot}
                key={`field-pill-${f.name}-${idx}`}
                expanded={forceExpanded || isSearchFieldsValueValid}
                onExpandToggle={() => onExpandToggleHandler(f.name)}
                isScrolling={isScrolling ?? false}
              >
                {childFields}
              </FieldPillGroupStyled>,
            );
          }
        } else {
          acc.push(
            <FieldPillStyled
              field={f}
              key={`${f.name}-${idx}`}
              renderFieldDropdown={isDragging => (
                <FieldDropdown
                  discoveryId={viz?.id}
                  field={f}
                  doMenuAction={key => onContextMenuAction(key, f)}
                  isScrolling={isScrolling ?? false}
                  isDragging={isDragging}
                />
              )}
            />,
          );
        }
        return acc;
      }, []);
  };

  const visibleFieldsRendered = renderFields(visibleFields);
  const invisibleFieldsRendered = renderFields(invisibleFields);

  if (virtualizationData.shouldUseVirtualization) {
    return (
      <div className={'field-list'} role={'list'} onScroll={handleScroll}>
        {keys(hideFields).length ===
          availableFieldCount - fieldsInPlay.length &&
          !isEmpty(searchFieldsValue) && (
            <div className='no-search-results'>
              {messages.layoutPanel.noMatches}
            </div>
          )}
        {virtualizationData.totalItemCount <= 0 && (
          <NoMatches>{messages.layoutPanel.noMatches}</NoMatches>
        )}
        <VirtualizedFieldList
          onContextMenuAction={onContextMenuAction}
          isScrolling={isScrolling}
          expandTimeFields={expandTimeFields}
          onExpandToggleHandler={onExpandToggleHandler}
        />
      </div>
    );
  }

  return (
    <div className={'field-list'} role={'list'} onScroll={handleScroll}>
      {keys(hideFields).length === availableFieldCount - fieldsInPlay.length &&
        !isEmpty(searchFieldsValue) && (
          <div className='no-search-results'>
            {messages.layoutPanel.noMatches}
          </div>
        )}
      {visibleFieldsRendered.length <= 0 && (
        <NoMatches>{messages.layoutPanel.noMatches}</NoMatches>
      )}
      {visibleFieldsRendered}
      {showHiddenFields && [
        <HiddenFieldsHeader key={'invisible-fields-header'}>
          {messages.layoutPanel.hiddenFields}
        </HiddenFieldsHeader>,
        invisibleFieldsRendered?.length === 0 ? (
          <NoMatches key={'no-matches'}>
            {messages.layoutPanel.noMatches}
          </NoMatches>
        ) : (
          invisibleFieldsRendered
        ),
      ]}
    </div>
  );
};
